import JSZip from "jszip";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api";
import { Button } from "../externalComponents/tailwind/button";
import { CustomerContext } from "../providers";

const getDownloadParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const tid = urlParams.get("tid");

  if (tid) {
    try {
      const decoded = JSON.parse(atob(tid));
      return {
        traceId: decoded.traceId ?? "",
        exportType: decoded.exportType ?? "all-contacts",
      };
    } catch (error) {
      console.error("Error decoding tid:", error);
      return {
        traceId: "",
        exportType: "all-contacts",
      };
    }
  }

  return {
    traceId: urlParams.get("traceId") ?? "",
    exportType: urlParams.get("exportType") ?? "all-contacts",
  };
};

export default function DownloadFile() {
  const navigate = useNavigate();
  const { user } = useContext(CustomerContext);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState("initial");

  const { traceId, exportType } = getDownloadParams();

  const handleDownloadFiles = async () => {
    setIsLoading(true);
    try {
      const threeHoursAgo = new Date();
      threeHoursAgo.setHours(threeHoursAgo.getHours() - 3);

      const files = await api.exportFile.findMany({
        filter: {
          traceId: { equals: traceId },
          customerId: { equals: user.customer.id },
          createdAt: { greaterThan: threeHoursAgo },
        },
        select: {
          file: true,
        },
      });

      if (!files || files.length === 0) {
        setDownloadStatus("error");
        setIsLoading(false);
        return;
      }

      const zip = new JSZip();

      for (const [index, fileRecord] of files.entries()) {
        const fileName =
          fileRecord.file?.fileName || `export_file_${index + 1}.csv`;
        const response = await fetch(fileRecord.file.url);
        const csvContent = await response.text();
        zip.file(fileName, csvContent);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `export_${exportType}_${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadStatus("success");
    } catch (error) {
      console.error("Error creating zip:", error);
      setDownloadStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const getExportTypeLabel = () => {
    switch (exportType) {
      case "all-contacts":
        return "all contacts";
      case "selected-contacts":
        return "selected contacts";
      case "campaign-stats-by-mail":
        return "campaign statistics";
      case "whatsapp-flow-responses":
        return "WhatsApp flow responses";
      case "inbound-conversations":
        return "inbound conversations";
      default:
        return "contacts";
    }
  };

  return (
    <div className="h-full flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full text-center px-6">
        {downloadStatus === "initial" && (
          <>
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">
              Download Export
            </h1>
            <p className="text-gray-600 mb-8">
              You are about to download a ZIP file containing your{" "}
              <strong>{getExportTypeLabel()}</strong> export.
            </p>
            <Button
              color="primaryButton"
              onClick={handleDownloadFiles}
              loading={isLoading}
              className="w-full h-12"
            >
              {isLoading ? "Preparing download..." : "Download ZIP file"}
            </Button>
          </>
        )}

        {downloadStatus === "success" && (
          <>
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">
              Download Successful!
            </h1>
            <p className="text-gray-600 mb-8">
              Your export has been downloaded successfully.
            </p>
            <Button
              color="primaryButton"
              onClick={() => navigate("/dashboard")}
              className="w-full h-12"
            >
              Back to Dashboard
            </Button>
          </>
        )}

        {downloadStatus === "error" && (
          <>
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">
              Download Failed
            </h1>
            <p className="text-gray-600 mb-8">
              The link to download files is invalid or expired.
            </p>
            <Button
              color="primaryButton"
              onClick={() => navigate("/dashboard")}
              className="w-full h-12"
            >
              Back to home page
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
